<script lang="ts" context="module">
  type HeroVariant = "center" | "left";
  type HeroLeft = {
    value: "left";
    label: "Left-aligned";
    key: "left";
  };
  type HeroRight = {
    value: "center";
    label: "Center-aligned";
    key: "center";
  };
  export interface HeroProps {
    videoUrl?: string;
    imageUrl?: string;
    hero1Text?: string;
    hero2Text?: string;
    button?: ButtonProps;
    buttons?: ButtonProps[];
    variant?: HeroVariant;
  }
</script>

<script lang="ts">
  import { isBackgroundPlayerPaused } from "../../../stores/playerStore.ts";
  import LayeredSection from "../../layout/LayeredSection.svelte";
  import { type ButtonProps } from "@elements/Button.svelte";
  import Buttons from "@components/buttons/Buttons.svelte";
  import { convertStatamicAssetToCDN } from "@utils/cdn";
  import Blocks from "../Blocks.svelte";
  import type { StatamicLink } from "@customTypes/statamic.js";
  import { twJoin } from "tailwind-merge";

  export let videoUrl: string = "";
  export let imageUrl: string = "";
  export let hero_logo: string = "";
  export let hero1Text: string = "";
  export let hero2Text: string = "";
  export let blocks: any = [];
  export let height: { value: keyof typeof heightClasses } = { value: "80" };

  // Statamic Properties
  export let hero_subtitle: string = "";
  hero2Text = hero_subtitle;

  export let background_video_url: string | StatamicLink | null = null;
  export let background_video_fallback_image: string | StatamicAsset | null =
    null;
  videoUrl =
    typeof background_video_url === "object"
      ? background_video_url?.url || ""
      : background_video_url || "";

  imageUrl = convertStatamicAssetToCDN(background_video_fallback_image?.id);
  let imageAsset = background_video_fallback_image;

  export let buttons: ButtonProps[] = [];
  export let hero_variant: HeroLeft | HeroRight = {
    value: "center",
    label: "Center-aligned",
    key: "center",
  };

  let video: HTMLVideoElement;

  $: video && ($isBackgroundPlayerPaused ? video.pause() : video.play());
  const heightClasses = {
    full: "min-h-screen ",
    "50": "min-h-[50vh]",
    "66": "max-h-[66vh]",
    "75": "min-h-[75vh]",
    "80": "min-h-[80vh]",
    "90": "min-h-[90vh]",
  };

  const variantContainerStyles = {
    center: twJoin(
      "text-white z-10 flex w-full flex-col items-center justify-center",
      heightClasses[height.value],
    ),
    left: "text-white text-left h-full min-h-screen w-full z-10 flex flex-col items-start justify-center",
  };
  const variantHero1Styles = {
    center: "wah_hero-1 text-white pb-6",
    left: "text-hero2 font-extrabold text-white normal-case mb-6",
  };
  const variantHero2Styles = {
    center: "wah_hero-2 ",
    left: "text-body-large font-light text-left max-w-screen-md ",
  };

  const variantContentStyles = {
    center: "w-full max-w-sm sm:max-w-full sm:w-3/5 lg:w-1/2",
    left: "md:max-w-screen-md ",
  };

  const backgroundImageOrVideo = videoUrl
    ? {
        backgroundVideo: { videoUrl, imageUrl },
      }
    : {
        fullscreenBackgroundImage: { asset: imageAsset },
      };
</script>

<LayeredSection
  id="hero"
  addClasses={twJoin(
    "box-border flex  items-start justify-center bg-cover bg-center text-center md:overflow-hidden",
  )}
  layers={[
    backgroundImageOrVideo,
    {
      backgroundCSS:
        "background: linear-gradient(rgba(0, 0, 0, 0) 70%, rgba(0, 0, 0, 1) );",
    },
  ]}
  bind:video
>
  <div class={`${variantContainerStyles[hero_variant.value]} container pt-32`}>
    <div class={variantContentStyles[hero_variant.value]}>
      <h1 class={variantHero1Styles["center"]}>
        {#if hero1Text}
          {hero1Text}
        {/if}
        {#if hero_logo}
          <img
            src={convertStatamicAssetToCDN(hero_logo.id)}
            alt={hero_logo.alt}
          />
        {/if}
      </h1>
      {#if blocks}
        <Blocks {blocks} />
      {/if}
      {#if hero_subtitle}
        <Blocks blocks={hero_subtitle} />
      {/if}

      {#if buttons}
        <Buttons {buttons} />
      {/if}
    </div>
  </div></LayeredSection
>
